import { createTheme, type ThemeOptions } from "@mui/material/styles";

// Import theme constants
import { THEME_CONSTANTS } from "./constants";

// Export theme constants and helpers
export {
  THEME_CONSTANTS,
  commonStyles,
  getResponsivePadding,
  getResponsiveSpacing,
} from "./constants";

// Enterprise SaaS color palette - Phase 1: Modern professional colors
const palette = {
  primary: {
    main: "#6366F1", // Soft indigo (similar to NIQ purple but more accessible)
    light: "#818CF8",
    dark: "#4F46E5",
    contrastText: "#FFFFFF",
  },
  secondary: {
    main: "#8B5CF6", // Complementary purple
    light: "#A78BFA",
    dark: "#7C3AED",
    contrastText: "#FFFFFF",
  },
  success: {
    main: "#10B981", // Keep existing green for consistency
    light: "#34D399",
    dark: "#059669",
  },
  warning: {
    main: "#F59E0B", // Keep existing orange
    light: "#FBBF24",
    dark: "#D97706",
  },
  error: {
    main: "#EF4444", // Keep existing red
    light: "#F87171",
    dark: "#DC2626",
  },
  info: {
    main: "#6366F1", // Match primary
    light: "#818CF8",
    dark: "#4F46E5",
  },
  background: {
    default: "#F8FAFC", // Slightly warmer, softer than pure gray
    paper: "#FFFFFF",
  },
  text: {
    primary: "#1E293B", // Slate gray for better readability
    secondary: "#64748B", // Softer secondary text
  },
  divider: "#E2E8F0", // Softer divider
};

// Responsive typography that scales based on device
const typography = {
  fontFamily: [
    "-apple-system",
    "BlinkMacSystemFont",
    '"Segoe UI"',
    "Roboto",
    '"Helvetica Neue"',
    "Arial",
    "sans-serif",
  ].join(","),
  h1: {
    fontSize: "1.75rem",
    fontWeight: 700,
    lineHeight: 1.2,
    "@media (min-width:768px)": {
      fontSize: "2.125rem",
    },
  },
  h2: {
    fontSize: "1.5rem",
    fontWeight: 600,
    lineHeight: 1.3,
    "@media (min-width:768px)": {
      fontSize: "1.875rem",
    },
  },
  h3: {
    fontSize: "1.25rem",
    fontWeight: 600,
    lineHeight: 1.4,
    "@media (min-width:768px)": {
      fontSize: "1.5rem",
    },
  },
  h4: {
    fontSize: "1.125rem",
    fontWeight: 600,
    lineHeight: 1.4,
    "@media (min-width:768px)": {
      fontSize: "1.25rem",
    },
  },
  h5: {
    fontSize: "1rem",
    fontWeight: 600,
    lineHeight: 1.5,
    "@media (min-width:768px)": {
      fontSize: "1.125rem",
    },
  },
  h6: {
    fontSize: "0.875rem",
    fontWeight: 600,
    lineHeight: 1.5,
    "@media (min-width:768px)": {
      fontSize: "1rem",
    },
  },
  body1: {
    fontSize: "0.875rem",
    lineHeight: 1.6,
    "@media (min-width:768px)": {
      fontSize: "1rem",
      lineHeight: 1.5,
    },
  },
  body2: {
    fontSize: "0.8125rem",
    lineHeight: 1.6,
    "@media (min-width:768px)": {
      fontSize: "0.875rem",
      lineHeight: 1.5,
    },
  },
  caption: {
    fontSize: "0.6875rem",
    lineHeight: 1.5,
    "@media (min-width:768px)": {
      fontSize: "0.75rem",
      lineHeight: 1.4,
    },
  },
};

const shape = {
  borderRadius: 4, // Clean rectangular corners
};

const spacing = THEME_CONSTANTS.spacing.sm;

const themeOptions: ThemeOptions = {
  palette,
  typography,
  shape,
  spacing,
  // Enhanced breakpoints for better responsive behavior
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600, // Bolder for modern look
          borderRadius: 4, // Clean rectangular corners
          transition: "all 0.2s ease-in-out",
          // Enhanced touch targets for mobile
          minHeight: 44,
          padding: "10px 20px", // More horizontal padding
          "@media (min-width:600px)": {
            minHeight: "auto",
            padding: "10px 24px",
          },
        },
        sizeSmall: {
          // Ensure small buttons are still touch-friendly on mobile
          minHeight: 36,
          padding: "8px 16px",
          fontSize: "0.875rem",

          "@media (min-width:600px)": {
            minHeight: "auto",
            padding: "6px 16px",
            fontSize: "0.8125rem",
          },
        },
        sizeMedium: {
          minHeight: 44,
          padding: "10px 20px",
          "@media (min-width:600px)": {
            minHeight: "auto",
            padding: "10px 24px",
          },
        },
        sizeLarge: {
          minHeight: 48,
          padding: "12px 28px",
          "@media (min-width:600px)": {
            minHeight: "auto",
            padding: "12px 32px",
          },
        },
        containedPrimary: {
          boxShadow: "0 2px 8px rgba(99, 102, 241, 0.24)",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(99, 102, 241, 0.32)",
            transform: "translateY(-1px)",
          },
          "&:active": {
            transform: "translateY(0)",
          },
        },
        outlined: {
          borderWidth: "1.5px",
          "&:hover": {
            borderWidth: "1.5px",
            backgroundColor: "rgba(99, 102, 241, 0.04)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 4, // Clean rectangular corners
          boxShadow:
            "0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03)", // Softer shadow
          border: "1px solid #E2E8F0", // Softer border
          transition: "all 0.2s ease-in-out", // Smooth transitions
          // Better mobile padding
          "& .MuiCardContent-root": {
            padding: "20px",
            "@media (min-width:600px)": {
              padding: "24px",
            },
            "@media (min-width:900px)": {
              padding: "28px",
            },
          },
          "&:hover": {
            boxShadow:
              "0 4px 12px rgba(99, 102, 241, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)", // Subtle purple tint on hover
            transform: "translateY(-2px)", // Subtle lift effect
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#FFFFFF",
          color: "#1E293B", // Updated to match new text primary
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)", // Softer shadow
          borderBottom: "1px solid #E2E8F0", // Subtle border
          // Enhanced height for better mobile usability
          minHeight: 56,
          "@media (min-width:600px)": {
            minHeight: 64,
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "#F8FAFC", // Updated to match new background
          borderRight: "1px solid #E2E8F0", // Updated to match new divider
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 4, // Clean rectangular corners
          margin: "2px 8px",
          transition: "all 0.2s ease-in-out",
          // Better touch targets for mobile
          minHeight: 48,
          padding: "12px 16px",
          "@media (min-width:600px)": {
            minHeight: 44,
            padding: "10px 16px",
          },
          "&.Mui-selected": {
            backgroundColor: "rgba(99, 102, 241, 0.08)", // Purple-tinted selection
            color: "#6366F1", // Purple text
            "&:hover": {
              backgroundColor: "rgba(99, 102, 241, 0.12)", // Darker purple on hover
            },
          },
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)", // Subtle hover
          },
        },
      },
    },
    // Enhanced form components for mobile
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputBase-root": {
            minHeight: 44,
            borderRadius: 4, // Clean rectangular corners
            "@media (min-width:600px)": {
              minHeight: "auto",
            },
          },
          "& .MuiInputBase-input": {
            padding: "12px 14px",
            "@media (min-width:600px)": {
              padding: "10px 14px",
            },
          },
          "& .MuiOutlinedInput-root": {
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#6366F1", // Purple focus ring
              borderWidth: "2px",
            },
          },
        },
      },
    },
    // Enhanced table responsiveness
    MuiTable: {
      styleOverrides: {
        root: {
          "@media (max-width:899px)": {
            fontSize: "0.75rem",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "8px",
          "@media (min-width:600px)": {
            padding: "12px 16px",
          },
        },
      },
    },
    // Enhanced dialog/modal responsiveness with proper sizing
    MuiDialog: {
      styleOverrides: {
        root: {
          "& .MuiDialog-container": {
            alignItems: "flex-end", // Default for mobile
            "@media (min-width:600px)": {
              alignItems: "center",
            },
          },
        },
        paper: {
          borderRadius: 4, // Clean rectangular corners for dialogs
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)", // Softer shadow
          margin: 8, // Default margin for mobile
          maxWidth: "95vw", // Default maxWidth for mobile
          width: "100%",
          // Mobile-first responsive approach
          "@media (max-width:599px)": {
            margin: 8,
            maxWidth: "calc(100vw - 16px)",
            borderRadius: "4px 4px 0 0",
            position: "fixed",
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
          },
          "@media (min-width:600px)": {
            margin: 16, // sm: 2 * 8px
            maxWidth: "90vw",
          },
          "@media (min-width:900px)": {
            margin: 24, // md: 3 * 8px
            maxWidth: "80vw",
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          borderBottom: "1px solid",
          borderColor: "#E2E8F0", // Updated to match new divider color
          paddingBottom: THEME_CONSTANTS.spacing.md * 8, // Convert to px
          marginBottom: 0,
          fontWeight: THEME_CONSTANTS.typography.fontWeight.semiBold,
          fontSize: "1.125rem",
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          paddingTop: THEME_CONSTANTS.spacing.md * 8, // Convert to px
          paddingBottom: THEME_CONSTANTS.spacing.md * 8,
          paddingLeft: THEME_CONSTANTS.spacing.md * 8,
          paddingRight: THEME_CONSTANTS.spacing.md * 8,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          borderTop: "1px solid",
          borderColor: "#E2E8F0", // Updated to match new divider color
          paddingTop: THEME_CONSTANTS.spacing.md * 8,
          paddingBottom: THEME_CONSTANTS.spacing.md * 8,
          paddingLeft: THEME_CONSTANTS.spacing.md * 8,
          paddingRight: THEME_CONSTANTS.spacing.md * 8,
          gap: THEME_CONSTANTS.spacing.sm * 8, // Convert to px
          justifyContent: "flex-end",
        },
      },
    },
    // Override Switch to use the DS teal accent token
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          "&.Mui-checked": {
            color: "var(--accent)",
            "& + .MuiSwitch-track": {
              backgroundColor: "var(--accent)",
              opacity: 0.65,
            },
          },
        },
      },
    },
    // Enhanced chip components for mobile
    MuiChip: {
      styleOverrides: {
        root: {
          height: 28,
          fontSize: "0.75rem",
          borderRadius: 5, // Subtle rounded corners
          fontWeight: 500,
          "@media (min-width:600px)": {
            height: 24,
            fontSize: "0.6875rem",
          },
        },
        sizeSmall: {
          height: 24,
          fontSize: "0.6875rem",
          borderRadius: 4, // Minimal rounding for small chips
          "@media (min-width:600px)": {
            height: 20,
            fontSize: "0.625rem",
          },
        },
        colorPrimary: {
          backgroundColor: "rgba(99, 102, 241, 0.1)", // Purple-tinted background
          color: "#6366F1", // Purple text
        },
        colorSecondary: {
          backgroundColor: "rgba(139, 92, 246, 0.1)", // Secondary purple-tinted background
          color: "#8B5CF6", // Secondary purple text
        },
      },
    },
  },
};

const theme = createTheme(themeOptions);

export default theme;
